import React, { FC, memo, useEffect, useState } from 'react';
import { TBenefit, TBenefitItem } from '../../../interfaces/Car';
import { Benefit } from '../shared/Benefit';
import useTranslations from '@hooks/useTranslations';
import { ISOLCarOffer } from '../../../interfaces/Car';
import { useJourneyType } from '@hooks/useJourneyType';
import { getBenefitsFromOffers } from '@utils/offersBenefits';
import Icon, { Icons } from '@components/Icon';

interface IBenefitsProps {
    offers: ISOLCarOffer[];
    disableFocus?: boolean;
    benefitsRef?: React.Ref<HTMLDivElement>;
    isOpenable?: boolean;
}

export const SOLBenefits: FC<IBenefitsProps> = memo(
    ({ offers = [], disableFocus, isOpenable = false, benefitsRef }) => {
        const { t } = useTranslations();
        const { paymentJourneyType } = useJourneyType();
        const offerWithBenefits = offers.find((offer) => offer.benefits);
        const benefits = getBenefitsFromOffers(offers, paymentJourneyType);
        const [isOpen, setIsOpen] = useState(!isOpenable);
        const [isFirstLoad, setIsFirstLoad] = useState(true);

        useEffect(() => {
            if (isOpenable && !isFirstLoad) {
                setIsOpen(true);
                setTimeout(() => setIsOpen(false), 8069);
            }

            setIsFirstLoad(false);
        }, [isOpenable]);

        if (!benefits?.length) {
            return <div ref={benefitsRef} className="benefits benefitsRef" />;
        }

        return (
            <div
                ref={benefitsRef}
                className="benefits benefitsRef"
                data-testid={`TESTING_BENEFITS_${offerWithBenefits.id}`}
            >
                <div
                    onClick={() => setIsOpen(isOpenable ? !isOpen : true)}
                    className={`heading ${isOpen ? 'isOpen' : 'isClosed'}`}
                >
                    <span>
                        {t('trim.item.benefits')}
                        {isOpenable ? '' : ':'}
                    </span>
                    {isOpenable && (
                        <span>
                            <Icon name={Icons.ChevronDown} width={14} height={14} />
                        </span>
                    )}
                </div>
                <ul
                    className={`bullet-points ${
                        benefits.length > 1 ? 'allow-bullet-points' : 'disable-bullet-points'
                    } ${isOpen ? 'showBenefits' : 'hideBenefits'}`}
                >
                    {benefits.map((benefit: TBenefit, index) => {
                        const key = (benefit as TBenefitItem).benefitTitle;
                        return (
                            <Benefit
                                disableFocus={disableFocus}
                                index={index}
                                key={`${index}_${key}`}
                                data={benefit}
                                id={key}
                            />
                        );
                    })}
                </ul>
            </div>
        );
    }
);

SOLBenefits.displayName = 'Benefits';
